import { Link, useLocation } from 'react-router-dom';
import { Button, cn } from '@sib/ui';
import { ChevronRight } from '@sib/ui/icons';
import type { SidebarMenuItem } from './Sidebar.utils';

interface SidebarItemProps extends SidebarMenuItem {
  open?: boolean;
  onItemClick?: (hasSubmenu: boolean) => void;
}

export const SidebarItem = ({
  icon: Icon,
  href,
  title,
  onClick,
  open,
  hasExpandSubmenuArrow,
  onExpandSubmenuClick,
  onItemClick,
  isExpandedSubmenu,
  ...props
}: SidebarItemProps) => {
  const { pathname } = useLocation();

  const isActive = pathname.startsWith(href?.toString() ?? '/#');
  const clickableItemsClassName = cn(
    'text-primary justify-start rounded-none font-normal no-underline w-full flex items-center p-0 gap-3 pr-3 pl-4 text-sm h-full '
  );
  const isExternal = href?.startsWith('http');

  const handleItemClick = (_?: React.MouseEvent) => {
    onItemClick?.(!!hasExpandSubmenuArrow);
    onClick?.();
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onExpandSubmenuClick?.();
  };

  return (
    <span
      className={cn(
        'group/sidebar-item hover:bg-secondary/10 relative flex h-11.5 items-center bg-transparent transition-all duration-300',
        isActive && 'bg-secondary/10 font-medium'
      )}
      {...props}
    >
      <span
        className={cn(
          'bg-secondary absolute left-0 h-full w-1 opacity-0 transition-all duration-300 group-hover/sidebar-item:opacity-100',
          isActive && 'opacity-100'
        )}
      />
      {onClick ? (
        <Button
          variant="ghost"
          className={clickableItemsClassName}
          key={title}
          onClick={handleItemClick}
        >
          <Icon className="size-6" />
          <span
            className={cn(
              'relative overflow-hidden whitespace-nowrap transition-all duration-300',
              open ? 'max-w-100' : 'max-w-0'
            )}
          >
            {title}
          </span>
        </Button>
      ) : (
        <Link
          key={href}
          to={href?.toString() ?? '/#'}
          title={title}
          onClick={handleItemClick}
          className={clickableItemsClassName}
          {...(isExternal && {
            target: '_blank',
            rel: 'noopener noreferrer',
          })}
        >
          <Icon className="size-6" />
          <span
            className={cn(
              'relative overflow-hidden whitespace-nowrap transition-all duration-300',
              open ? 'max-w-100' : 'max-w-0'
            )}
          >
            {title}
          </span>
        </Link>
      )}
      {hasExpandSubmenuArrow && open && (
        <Button
          disabled={!isActive}
          variant="ghost"
          size="sm"
          className="absolute right-2 z-10 h-auto p-1 hover:bg-transparent"
          onClick={handleExpandClick}
        >
          <ChevronRight
            className={cn(
              'size-6 transition-transform duration-300',
              isExpandedSubmenu && 'rotate-180'
            )}
          />
        </Button>
      )}
    </span>
  );
};
